import { createStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { z } from 'zod';
import { StoreSlice, IMapEntry } from '@/store';
import {
  EntryItemType,
  EntryType,
  ReservedNodeType,
  TreeItem,
  isTreeItem,
} from '@morph-mapper/types';
import { DirtyType } from '@/types';

export interface EntryProps {
  id: string;
}

export type InitEntryProps = EntryProps;

export interface EntryState extends EntryProps {
  getId: () => string;
  getName: () => string | undefined;
  getIteratorId: () => string | undefined;
  getIteratorType: () => EntryType | undefined;
  setIteratorType: (type: EntryItemType) => void;
  hasIterator: () => boolean;
  deleteIterator: () => void;
  getDeclareVariableId: () => string | undefined;
  ensureDeclareVariable: () => string;
  getVariableIds: () => string[];
  getVariable: (id: string) => TreeItem;
  createVariable: (name: string) => void;
  deleteVariable: (id: string) => void;
  fillSuggestion: (code: string) => void;
}

export type EntryStore = ReturnType<typeof createEntryStore>;

export const createEntryStore = (
  global: StoreSlice,
  initProps: InitEntryProps,
) => {
  return createStore<EntryState & StoreSlice & IMapEntry>()(
    immer((_set, get) => {
      return {
        ...global,
        // TODO: we probably want to differentiate between a map and item,
        // as we can now call functions which crash the application
        ...global.entries.entry(initProps.id, EntryType.Map),
        ...initProps,

        getIteratorId: () => {
          return get().children().getId(ReservedNodeType.Iterator);
        },
        getIteratorType: () => {
          const iteratorId = get().children().getId(ReservedNodeType.Iterator);
          if (!iteratorId) return undefined;
          return get().entries.getType(iteratorId);
        },
        setIteratorType: (type: EntryItemType) => {
          const iteratorId = get().children().getId(ReservedNodeType.Iterator);
          if (!iteratorId) throw new Error('No Iterator ID found.');
          get().entries.setType(iteratorId, type);
        },
        hasIterator: () => {
          return !!get().children().getId(ReservedNodeType.Iterator);
        },
        deleteIterator: () => {
          const iteratorId = get().children().getId(ReservedNodeType.Iterator);
          if (!iteratorId) throw new Error('No Iterator ID found.');
          get().entries.delete(iteratorId);
          get().entries.addDirtyAll(DirtyType.Underlying);
        },
        getDeclareVariableId: () => {
          return get().children().getId(ReservedNodeType.DeclareVariable);
        },
        ensureDeclareVariable: () => {
          let declareEntryId = get()
            .children()
            .getId(ReservedNodeType.DeclareVariable);

          if (!declareEntryId) {
            declareEntryId = get().entries.createMap({
              key: ReservedNodeType.DeclareVariable,
              name: ReservedNodeType.DeclareVariable,
              parentId: get().id,
              formerOutputType: 'group',
              outputType: 'group',
            });
            get().linkChild(declareEntryId);
          }

          return declareEntryId;
        },
        getVariableIds: () => {
          const declareEntryId = get()
            .children()
            .getId(ReservedNodeType.DeclareVariable);
          if (!declareEntryId) return [];

          return get()
            .entries.children(declareEntryId!)
            .getIds()
            .filter((id) => get().entries.getType(id) !== EntryType.Internal);
        },
        createVariable: (name) => {
          let declareEntryId = get()
            .children()
            .getId(ReservedNodeType.DeclareVariable);

          if (!declareEntryId) {
            declareEntryId = get().entries.createMap({
              key: ReservedNodeType.DeclareVariable,
              name: ReservedNodeType.DeclareVariable,
              parentId: get().id,
              formerOutputType: 'group',
              outputType: 'group',
            });
            get().linkChild(declareEntryId);
          }

          const id = get().entries.createItem(
            {
              key: name,
              name,
              parentId: declareEntryId,
              validation: z.any(),
            },
            EntryType.Simple,
          );

          get().entries.children(declareEntryId).set(id, name);
        },
        getVariable: (id) => {
          const variableEntry = get().entries.getById(id);
          if (!isTreeItem(variableEntry)) throw 'Incorrect type';

          return variableEntry;
        },
        deleteVariable: (id) => {
          const declareEntryId = get()
            .children()
            .getId(ReservedNodeType.DeclareVariable);
          if (!declareEntryId) throw new Error('Cannot delete variable');

          get().entries.delete(id);
        },
        fillSuggestion: (code: string) => {
          // check if iterator exists
          let iteratorId = get().children().getId(ReservedNodeType.Iterator);
          // if not create one
          if (!iteratorId) {
            iteratorId = get().entries.createItem(
              {
                key: ReservedNodeType.Iterator,
                parentId: get().id,
              },
              EntryType.Code,
            );
            get().linkChild(iteratorId);
          }
          // add suggestion
          get().entries.setType(iteratorId, EntryType.Code);
          get().entries.setTemplateValue(iteratorId, code);
          get().entries.addDirtyAll(DirtyType.Underlying);
        },
      };
    }),
  );
};
